import axios, { Axios } from 'axios'
import history from 'shared/helpers/history'
import { requestInterceptor, responseInterceptor } from './interceptors'

export const INDEXED_DB_TABLE_NAME = `${window?.location?.host}_REQUESTS`
export const INDEXED_DB_TABLE_VERSION = 1
export const INDEXED_DB_GET_TABLE = 'REST_MANAGER_GET'

const NETWORK_ERROR_MESSAGE = 'Network Error'
const REQUEST_ERROR_MESSAGE = 'Request Error'
export const LARGE_IMAGE = 'LARGE_IMAGE'

class axiosInstance {
  public axiosInstance: Axios

  constructor(endpointToSet: string) {
    this.axiosInstance = axios.create({
      baseURL: endpointToSet,
      headers: {
        'Content-Type': 'application/json'
      }
    })
    this.axiosInstance.interceptors.request.use(requestInterceptor)
    this.axiosInstance.interceptors.response.use(responseInterceptor)
  }

  async get(path = '/', cacheFirst = false): Promise<any> {
    let res: any
    if (!navigator.onLine) {
      res = await this.getFromCache(path)
    }
    if (navigator.onLine && !cacheFirst) {
      res = await this.networkRequest('get', path)
    }
    if (navigator.onLine && cacheFirst) {
      res = await this.cacheFirst(path)
    }
    return res
  }

  async post(path = '/', payload: any): Promise<any> {
    let res: any
    if (navigator.onLine) {
      res = await this.networkRequest('post', path, payload)
    }
    if (!navigator.onLine) {
      history.push('/network-error')
    }
    return res
  }

  async networkRequest(method: 'get' | 'post', path = '/', payload?: any) {
    try {
      let data: any

      if (method === 'get') {
        data = await this.axiosInstance.get(path)
        this.saveToCache(path, data)
      }

      if (method === 'post') {
        data = await this.axiosInstance.post(path, payload)
      }

      return data
    } catch (error: any) {
      if (error.message === NETWORK_ERROR_MESSAGE) {
        history.push('/network-error')
      } else if (
        error?.response?.data?.message?.toLowerCase?.()?.includes?.('request entity too large')
      ) {
        throw new Error('LARGE_IMAGE')
      } else {
        throw new Error(REQUEST_ERROR_MESSAGE)
      }
    }
  }

  async cacheFirst(url = '/') {
    const thisNetworkRequest = this.networkRequest.bind(this)
    const promise = new Promise(function (resolve, reject) {
      const indexed = indexedDB.open(INDEXED_DB_TABLE_NAME, INDEXED_DB_TABLE_VERSION)
      indexed.onupgradeneeded = () => {
        indexed.result.createObjectStore(INDEXED_DB_GET_TABLE, {
          keyPath: 'request'
        })
      }
      indexed.onsuccess = () => {
        const requests = indexed.result
          .transaction(INDEXED_DB_GET_TABLE, 'readonly')
          .objectStore(INDEXED_DB_GET_TABLE)
        const cursorRequest = requests.get(url)
        cursorRequest.onsuccess = async (e: any) => {
          if (e?.target?.result?.response) {
            thisNetworkRequest('get', url)
            resolve(JSON.parse(e.target.result.response))
          } else {
            try {
              const res = await thisNetworkRequest('get', url)
              resolve(res)
            } catch (e: any) {
              reject(e)
            }
          }
        }
        cursorRequest.onerror = async (e: any) => {
          const res = await thisNetworkRequest('get', url)
          resolve(res)
        }
      }
      indexed.onerror = async () => {
        const res = await thisNetworkRequest('get', url)
        resolve(res)
      }
    })
    const result = await promise
    return result
  }

  async getFromCache(url = '/') {
    const promise = new Promise(function (resolve) {
      const indexed = indexedDB.open(INDEXED_DB_TABLE_NAME, INDEXED_DB_TABLE_VERSION)
      indexed.onupgradeneeded = () => {
        indexed.result.createObjectStore(INDEXED_DB_GET_TABLE, {
          keyPath: 'request'
        })
      }
      indexed.onsuccess = () => {
        const requests = indexed.result
          .transaction(INDEXED_DB_GET_TABLE, 'readonly')
          .objectStore(INDEXED_DB_GET_TABLE)
        const cursorRequest = requests.get(url)
        cursorRequest.onsuccess = (e: any) => {
          if (e?.target?.result?.response) {
            resolve(JSON.parse(e.target.result.response))
          } else {
            resolve(null)
          }
        }
        cursorRequest.onerror = (e: any) => {
          history.push('/network-error')
        }
      }
    })
    const result = await promise
    return result
  }

  async saveToCache(path = '/', dataToSave: any) {
    const indexed = indexedDB.open(INDEXED_DB_TABLE_NAME, INDEXED_DB_TABLE_VERSION)
    indexed.onupgradeneeded = () => {
      indexed.result.createObjectStore(INDEXED_DB_GET_TABLE, {
        keyPath: 'request'
      })
    }
    indexed.onsuccess = () => {
      const posts = indexed.result
        .transaction(INDEXED_DB_GET_TABLE, 'readwrite')
        .objectStore(INDEXED_DB_GET_TABLE)
      if (dataToSave) {
        posts.put({
          request: path || '/',
          response: JSON.stringify(dataToSave)
        })
      }
    }
  }
}

export const instance = new axiosInstance(process.env.REACT_APP_API_ENDPOINT as string)
